import { defineComponent, ref, useAttrs, computed, watch, onMounted, onUnmounted, resolveDirective, createElementBlock, openBlock, normalizeStyle, normalizeClass, unref, createCommentVNode, createVNode, withDirectives, toDisplayString, mergeProps, withCtx, createElementVNode, renderSlot, Fragment, renderList, withModifiers, isRef, createTextVNode, vShow } from "vue";
import PDropdown from "../p-dropdown.js";
import { _ as _sfc_main$1 } from "./p-icon.js";
import PInputSearch from "../p-input-search.js";
import { SIZES } from "../p-select-list.js";
import { useSelectList } from "../useSelectList.js";
import { useInputClasses } from "../useInputClasses.js";
import { toString } from "../string.js";
import { splitStringForHighlight } from "../text.js";
import { omit } from "lodash-es";
const _imports_0 = "data:image/svg+xml,%3csvg%20width='18'%20height='12'%20viewBox='0%200%2018%2012'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M16.1383%200.166992L6.30411%209.83366L1.69828%205.27533L0.526611%206.46033L5.71578%2011.597C5.87174%2011.7509%206.08205%2011.8372%206.3012%2011.8372C6.52034%2011.8372%206.73065%2011.7509%206.88661%2011.597L17.3033%201.35366L16.1383%200.166992Z'%20fill='%231A123B'%20/%3e%3c/svg%3e";
const _hoisted_1 = ["data-has-error"];
const _hoisted_2 = { class: "truncate text-left text-p-gray-40" };
const _hoisted_3 = {
  key: 0,
  class: "flex flex-wrap gap-1.5 pr-8"
};
const _hoisted_4 = {
  class: "max-w-[200px] truncate",
  "data-test": "pill-text"
};
const _hoisted_5 = ["onClick"];
const _hoisted_6 = {
  key: 1,
  class: "truncate text-left"
};
const _hoisted_7 = {
  key: 0,
  class: "mt-3 px-3"
};
const _hoisted_8 = { class: "text-p-purple-60" };
const _hoisted_9 = { class: "flex flex-row" };
const _hoisted_10 = ["onClick"];
const _hoisted_11 = ["title"];
const _hoisted_12 = {
  key: 0,
  class: "ml-auto fill-p-purple-60 pl-2",
  src: _imports_0
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  ...{
    name: "PDropdownSelect",
    inheritAttrs: false
  },
  __name: "p-dropdown-select",
  props: {
    modelValue: {
      type: [String, Number, Boolean, Array, Object],
      default: null
    },
    /**
     * `p-dropdown-select v-model normally works with a string or an array of strings (for multiple mode).
     * Setting this prop to `true` will switch the behavior to an object (or array of objects) for the v-model value.
     * e.g `:value="{ value: 1, text: 'option text' }"`
     */
    valueIsObject: {
      type: Boolean,
      default: false
    },
    /**
     * Can be an array of objects or array of strings. When using objects, will look for a text - value pair.
     * This can be changed using the item-text, item-value props.
     */
    items: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ""
    },
    errorMsg: {
      type: String,
      default: ""
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "md",
      validator(value) {
        return Object.keys(SIZES).includes(value);
      }
    },
    /**
     * Set property of **items**'s text value
     */
    itemText: {
      type: String,
      default: "text"
    },
    /**
     * Set property of **items**'s value - must be primitive.
     */
    itemValue: {
      type: [String, Number],
      default: "value"
    },
    /**
     * itemSize sets the list item's height and it's REQUIRED if a custom item template is used
     * https://github.com/Akryum/vue-virtual-scroller/tree/v1/packages/vue-virtual-scroller#props
     */
    itemSize: {
      type: Number,
      default: null
    },
    /**
     * The number of items visible in the list
     */
    itemsVisible: {
      type: Number,
      default: 7
    },
    dropdownMenuAttrs: {
      type: Object,
      default: () => ({})
    },
    dropdownMenuClass: {
      type: String,
      default: "overflow-x-hidden"
    },
    dropdownMenuStyle: {
      type: Object,
      default: () => ({})
    },
    pDropdownProps: {
      type: Object,
      default: () => ({})
    },
    searchable: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    /**
     * Enables multiple selection
     */
    multiple: {
      type: Boolean,
      default: false
    },
    /**
     * Dropdown button text
     */
    placeholder: {
      type: String,
      default: ""
    },
    /**
     * Placeholder for the search field
     */
    placeholderSearch: {
      type: String,
      default: ""
    },
    /**
     * Setting it to `true` will display the selected options at the top of the list
     */
    selectedTopShown: {
      type: Boolean,
      default: false
    },
    /**
     * Specify the key in the object to be used as item disabled prop.
     */
    disabledBy: {
      type: String,
      default: "disabled"
    },
    /**
     * Enables the ability to create new items when no search results are found
     */
    creatable: {
      type: Boolean,
      default: false
    },
    /**
     * Shows selected items as pills in the button when multiple selection is enabled
     */
    pills: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "select", "create"],
  setup(__props, { emit: __emit }) {
    const emit = __emit;
    const props = __props;
    const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const nextLoop = (ms) => new Promise((resolve) => setTimeout(resolve, 0));
    const P_DROPDOWN_DEFAULTS = {
      triggerStyle: { display: "block" },
      triggers: [],
      distance: 2,
      placement: "bottom-end",
      noAutoFocus: true,
      enableArrowNavigation: false
    };
    const CLEAR_BUTTON_SPACING = {
      sm: "right-8 text-sm top-[0.3rem]",
      md: "right-9 text-base top-[0.5rem]",
      lg: "right-10 text-lg top-[0.7rem]"
    };
    const PILL_SIZE = {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg"
    };
    const PILL_SELECT_SPACING = {
      sm: "min-h-[2rem] py-1 bg-[position:right_1rem_top_0.7rem]",
      md: "min-h-[2.5rem] py-2 bg-[position:right_1rem_top_0.9rem]",
      lg: "min-h-[3rem] py-2 bg-[position:right_1rem_top_1.1rem]"
    };
    const width = ref("auto");
    const listItemStyle = ref({ paddingTop: 0, paddingBottom: 0 });
    const dropdownShow = ref(false);
    let observer = null;
    const scrollerStyle = ref({ maxHeight: "none" });
    const formControl = ref(null);
    const inputSearch = ref(null);
    const actionsContainer = ref(null);
    const $attrs = useAttrs();
    const virtualizerRef = ref(null);
    const { labelClasses, selectClasses, errorMsgClasses } = useInputClasses(props);
    const {
      LIST_ITEM_CLASS,
      LIST_ITEM_ACTIVE_CLASS,
      LIST_ITEM_DISABLED_CLASS,
      selectedItems,
      computedItems,
      computedItemSize,
      computedInsideSelected,
      internalItems,
      internalValue,
      search,
      rowVirtualizer,
      getValue,
      getText,
      isSelected,
      isDisabled,
      setupNavigationSvc,
      destroyNavigationSvc,
      select,
      onMousemove,
      scrollToActiveItem,
      putSelectedItemsOnTop,
      selectAll,
      clearAll
    } = useSelectList(props, inputSearch, virtualizerRef, emit);
    const menuAttrs = computed(() => omit(props.dropdownMenuAttrs, ["class", "style"]));
    const menuStyle = computed(() => ({ ...props.dropdownMenuStyle, width: width.value }));
    const attrs = computed(() => {
      const { class: classes, style: style2, ...rest } = $attrs;
      return rest;
    });
    const style = computed(() => $attrs.style);
    const selectableItemsCount = computed(
      () => internalItems.value.filter((item) => !isDisabled(item) || isSelected(item[props.itemValue])).length
    );
    watch(
      dropdownShow,
      (nV) => {
        nV && putSelectedItemsOnTop();
      },
      {
        immediate: true
      }
    );
    onMounted(() => {
      observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const rect = entry.contentRect;
          width.value = rect.width + "px" || "auto";
        });
      });
      observer.observe(formControl.value);
    });
    onUnmounted(() => {
      formControl.value && observer && observer.unobserve(formControl.value);
    });
    const onShow = async () => {
      var _a, _b;
      const actionsContainerHeight = ((_a = actionsContainer.value) == null ? void 0 : _a.clientHeight) || 0;
      scrollerStyle.value = { maxHeight: `${computedItemSize.value * props.itemsVisible - actionsContainerHeight}px` };
      search.value = "";
      setupNavigationSvc();
      if (props.searchable) {
        await nextLoop();
        await nextFrame();
        (_b = inputSearch.value) == null ? void 0 : _b.$el.querySelector("input").focus();
      }
      scrollToActiveItem();
    };
    const onHide = () => {
      var _a, _b;
      dropdownShow.value = false;
      destroyNavigationSvc();
      (_b = (_a = formControl.value) == null ? void 0 : _a.querySelector("button")) == null ? void 0 : _b.focus();
    };
    const handleCreate = () => {
      emit("create", search.value);
      dropdownShow.value = false;
    };
    return (_ctx, _cache) => {
      const _directive_close_popper = resolveDirective("close-popper");
      return openBlock(), createElementBlock("div", {
        ref_key: "formControl",
        ref: formControl,
        class: normalizeClass([{ hidden: unref($attrs).hidden }, unref($attrs).class]),
        "data-has-error": !!__props.errorMsg,
        style: normalizeStyle(style.value),
        "aria-label": "Dropdown select"
      }, [
        __props.label ? (openBlock(), createElementBlock("label", {
          key: 0,
          class: normalizeClass(unref(labelClasses))
        }, toDisplayString(__props.label), 3)) : createCommentVNode("", true),
        createVNode(PDropdown, mergeProps({ shown: dropdownShow.value }, { ...P_DROPDOWN_DEFAULTS, ...__props.pDropdownProps }, {
          class: "relative flex w-full items-center justify-center",
          onApplyShow: onShow,
          onApplyHide: onHide
        }), {
          popper: withCtx(() => [
            createElementVNode("div", mergeProps(menuAttrs.value, {
              class: __props.dropdownMenuClass,
              style: menuStyle.value,
              "p-select-list": "",
              role: "listbox"
            }), [
              __props.multiple || __props.searchable ? (openBlock(), createElementBlock("div", _hoisted_7, [
                __props.multiple ? (openBlock(), createElementBlock("div", {
                  key: 0,
                  ref_key: "actionsContainer",
                  ref: actionsContainer,
                  class: "flex flex-row justify-between text-xs font-semibold text-primary"
                }, [
                  createElementVNode("p", _hoisted_8, toDisplayString(unref(computedItems).length) + " items", 1),
                  createElementVNode("div", _hoisted_9, [
                    unref(computedItems).length === unref(internalItems).length ? (openBlock(), createElementBlock("a", {
                      key: 0,
                      class: normalizeClass([
                        unref(selectedItems).length !== unref(internalItems).length ? "cursor-pointer" : "pointer-events-none opacity-50"
                      ]),
                      onClick: _cache[2] || (_cache[2] = //@ts-ignore
                      (...args) => unref(selectAll) && unref(selectAll)(...args))
                    }, " Select all ", 2)) : (openBlock(), createElementBlock("a", {
                      key: 1,
                      class: normalizeClass([unref(computedInsideSelected) ? "pointer-events-none opacity-50" : "cursor-pointer"]),
                      onClick: _cache[3] || (_cache[3] = //@ts-ignore
                      (...args) => unref(selectAll) && unref(selectAll)(...args))
                    }, " Select all filtered ", 2)),
                    _cache[7] || (_cache[7] = createElementVNode("span", { class: "px-1 leading-none" }, ".", -1)),
                    createElementVNode("a", {
                      class: normalizeClass([unref(selectedItems).length ? "cursor-pointer" : "pointer-events-none opacity-50"]),
                      onClick: _cache[4] || (_cache[4] = //@ts-ignore
                      (...args) => unref(clearAll) && unref(clearAll)(...args))
                    }, " Clear all ", 2)
                  ])
                ], 512)) : createCommentVNode("", true),
                __props.searchable ? (openBlock(), createElementBlock("div", {
                  key: 1,
                  class: normalizeClass([{ "mt-3": __props.multiple }])
                }, [
                  createVNode(PInputSearch, {
                    ref_key: "inputSearch",
                    ref: inputSearch,
                    modelValue: unref(search),
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => isRef(search) ? search.value = $event : null),
                    size: __props.size,
                    placeholder: __props.placeholderSearch
                  }, null, 8, ["modelValue", "size", "placeholder"])
                ], 2)) : createCommentVNode("", true)
              ])) : createCommentVNode("", true),
              createElementVNode("div", {
                ref_key: "virtualizerRef",
                ref: virtualizerRef,
                style: normalizeStyle(scrollerStyle.value),
                class: normalizeClass(["overflow-y-auto", { "mt-3": __props.multiple || __props.searchable }])
              }, [
                createElementVNode("div", {
                  class: "relative w-full",
                  "p-select-list-options": "",
                  style: normalizeStyle({ height: `${unref(rowVirtualizer).getTotalSize()}px` }),
                  onMousemove: _cache[6] || (_cache[6] = //@ts-ignore
                  (...args) => unref(onMousemove) && unref(onMousemove)(...args))
                }, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(unref(rowVirtualizer).getVirtualItems(), (row) => {
                    return openBlock(), createElementBlock("div", {
                      key: row.index,
                      class: "absolute left-0 top-0 w-full",
                      "p-select-list-option": "",
                      role: "option",
                      style: normalizeStyle({ height: `${row.size}px`, transform: `translateY(${row.start}px)` })
                    }, [
                      withDirectives((openBlock(), createElementBlock("div", {
                        class: normalizeClass([
                          unref(LIST_ITEM_CLASS),
                          {
                            [unref(LIST_ITEM_ACTIVE_CLASS)]: unref(isSelected)(unref(getValue)(row.index)),
                            [unref(LIST_ITEM_DISABLED_CLASS)]: unref(isDisabled)(unref(computedItems)[row.index])
                          }
                        ]),
                        "p-select-list-option-item": "",
                        style: normalizeStyle(listItemStyle.value),
                        onClick: ($event) => unref(select)($event, unref(getValue)(row.index))
                      }, [
                        createElementVNode("div", {
                          class: normalizeClass(["flex items-center", { [unref(SIZES)[__props.size]]: !__props.itemSize }])
                        }, [
                          renderSlot(_ctx.$slots, "item", {
                            item: unref(computedItems)[row.index],
                            isItemSelected: unref(isSelected)(unref(getValue)(row.index)),
                            itemTextSplit: unref(splitStringForHighlight)(unref(toString)(unref(getText)(row.index)), unref(search))
                          }, () => [
                            createElementVNode("div", {
                              class: "truncate text-p-purple-60",
                              title: unref(toString)(unref(getText)(row.index))
                            }, [
                              (openBlock(true), createElementBlock(Fragment, null, renderList(unref(splitStringForHighlight)(unref(toString)(unref(getText)(row.index)), unref(search)), (str, index) => {
                                return openBlock(), createElementBlock("span", {
                                  key: `${unref(getValue)(row.index)}_${index}`,
                                  class: normalizeClass({ "bg-p-blue-20": index === 1 })
                                }, toDisplayString(str), 3);
                              }), 128))
                            ], 8, _hoisted_11)
                          ]),
                          unref(isSelected)(unref(getValue)(row.index)) ? (openBlock(), createElementBlock("img", _hoisted_12)) : createCommentVNode("", true)
                        ], 2)
                      ], 14, _hoisted_10)), [
                        [_directive_close_popper, !__props.multiple]
                      ])
                    ], 4);
                  }), 128))
                ], 36),
                !unref(computedItems).length ? renderSlot(_ctx.$slots, "no-items", { key: 0 }, () => [
                  createElementVNode("div", {
                    class: normalizeClass(["flex items-center justify-center", unref(SIZES)[__props.size]])
                  }, [
                    __props.creatable && unref(search) ? (openBlock(), createElementBlock("button", {
                      key: 0,
                      class: "hover:text-primary-hover flex items-center gap-2 font-medium text-p-blue-40",
                      onClick: handleCreate
                    }, [
                      createVNode(_sfc_main$1, { icon: "fe:plus-circle" }),
                      createTextVNode(" Add '" + toDisplayString(unref(search)) + "' ", 1)
                    ])) : __props.creatable ? (openBlock(), createElementBlock(Fragment, { key: 1 }, [
                      createTextVNode("No items found. Type to add")
                    ], 64)) : (openBlock(), createElementBlock(Fragment, { key: 2 }, [
                      createTextVNode("No items found")
                    ], 64))
                  ], 2)
                ]) : createCommentVNode("", true)
              ], 6)
            ], 16)
          ]),
          default: withCtx(() => [
            createElementVNode("button", mergeProps(attrs.value, {
              ref: "button",
              type: "button",
              class: [
                "w-full",
                unref(selectClasses),
                dropdownShow.value ? "border-primary" : "",
                { "box-border h-auto items-start": __props.multiple && __props.pills && unref(selectedItems).length > 0 },
                PILL_SELECT_SPACING[__props.size]
              ],
              role: "button",
              "aria-haspopup": "listbox",
              onClick: _cache[1] || (_cache[1] = ($event) => dropdownShow.value = !dropdownShow.value)
            }), [
              !unref(internalValue).length || !unref(selectedItems).length ? renderSlot(_ctx.$slots, "placeholder", { key: 0 }, () => [
                createElementVNode("div", _hoisted_2, toDisplayString(__props.placeholder || " "), 1)
              ]) : renderSlot(_ctx.$slots, "selected-item", {
                key: 1,
                item: __props.multiple ? unref(selectedItems) : unref(selectedItems)[0]
              }, () => [
                __props.multiple && __props.pills ? (openBlock(), createElementBlock("div", _hoisted_3, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(unref(selectedItems), (item) => {
                    return openBlock(), createElementBlock("div", {
                      key: String(item[__props.itemValue]),
                      class: normalizeClass(["flex items-center gap-1 rounded bg-p-gray-10 px-2 text-p-gray-50", [PILL_SIZE[__props.size]]]),
                      "data-test": "selected-pill"
                    }, [
                      createElementVNode("span", _hoisted_4, toDisplayString(item[__props.itemText]), 1),
                      createElementVNode("button", {
                        type: "button",
                        class: "flex items-center justify-center text-p-gray-40 hover:text-p-gray-60",
                        "aria-label": "Remove item",
                        "data-test": "pill-remove",
                        onClick: withModifiers(($event) => unref(select)($event, item[__props.itemValue]), ["stop"])
                      }, [
                        createVNode(_sfc_main$1, {
                          icon: "fe:close",
                          class: "h-3.5 w-3.5"
                        })
                      ], 8, _hoisted_5)
                    ], 2);
                  }), 128))
                ])) : (openBlock(), createElementBlock("div", _hoisted_6, toDisplayString(__props.multiple ? unref(selectedItems).length === selectableItemsCount.value ? "All options selected" : `${unref(selectedItems).length} option${unref(selectedItems).length > 1 ? "s" : ""} selected` : unref(selectedItems)[0][__props.itemText]), 1))
              ]),
              __props.clearable && unref(selectedItems).length ? (openBlock(), createElementBlock("button", {
                key: 2,
                class: normalizeClass(["absolute right-9 flex h-6 items-center justify-center text-base text-p-gray-40 hover:text-p-gray-60", [CLEAR_BUTTON_SPACING[__props.size]]]),
                "aria-label": "Clear selection",
                onClick: _cache[0] || (_cache[0] = withModifiers(
                  //@ts-ignore
                  (...args) => unref(clearAll) && unref(clearAll)(...args),
                  ["stop"]
                ))
              }, [
                createVNode(_sfc_main$1, { icon: "fe:close" })
              ], 2)) : createCommentVNode("", true)
            ], 16)
          ]),
          _: 3
        }, 16, ["shown"]),
        withDirectives(createElementVNode("div", {
          class: normalizeClass(unref(errorMsgClasses))
        }, toDisplayString(__props.errorMsg), 3), [
          [vShow, __props.errorMsg]
        ])
      ], 14, _hoisted_1);
    };
  }
});
export {
  _imports_0 as _,
  _sfc_main as a
};
